import { ogImageMeta } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { BEST_GROUPS, BEST_BY_SLUG, BEST_LISTS } from "@/lib/library-best";
import { breadcrumbJsonLd } from "@/components/library-hub";
import { SiteFooter } from "@/components/site-footer";

const TITLE = "Best-of round-ups — hand-picked tool lists | The Vibeary";
const DESC =
  "Opinionated round-ups of the best free design tools, component libraries, animation engines, AI agent tooling and creative resources in The Vibeary.";
const URL = "https://vibing.inc/library/best";

export const Route = createFileRoute("/library/best/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: "Best-of round-ups — The Vibeary" },
      ...ogImageMeta("library-best", "Share card: best-of round-ups"),
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Best-of round-ups",
          url: URL,
          description: DESC,
          hasPart: BEST_LISTS.map((l) => ({
            "@type": "ItemList",
            name: l.title,
            url: `${URL}/${l.slug}`,
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: breadcrumbJsonLd([
          { name: "Home", item: "https://vibing.inc/" },
          { name: "The Vibeary", item: "https://vibing.inc/library" },
          { name: "Round-ups", item: URL },
        ]),
      },
    ],
  }),
  component: BestIndex,
});

function BestIndex() {
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <main id="main" className="mx-auto max-w-6xl px-5 py-10 md:px-8 md:py-16">
        <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground">
          <ol className="flex flex-wrap items-center gap-2">
            <li>
              <Link to="/" className="transition-colors hover:text-foreground">
                Home
              </Link>
            </li>
            <li aria-hidden>/</li>
            <li>
              <Link to="/library" className="transition-colors hover:text-foreground">
                The Vibeary
              </Link>
            </li>
            <li aria-hidden>/</li>
            <li className="text-foreground">Round-ups</li>
          </ol>
        </nav>

        <p className="mt-8 text-xs uppercase tracking-[0.18em] text-muted-foreground">
          The Vibeary — round-ups
        </p>
        <h1
          className="mt-4 font-display tracking-[-0.035em] leading-[1.04] max-w-[20ch]"
          style={{ fontSize: "clamp(2rem, 4.6vw, 3.6rem)" }}
        >
          Best-of round-ups
        </h1>
        <p className="mt-6 max-w-[62ch] text-base md:text-lg leading-relaxed text-muted-foreground text-pretty">
          {BEST_LISTS.length} opinionated lists pulled from the library: the
          tools worth bookmarking for a specific job, with the criteria that got
          them there and a note on why each one earned its place. Every entry
          links to its gem page, its shelf and its source.
        </p>

        {BEST_GROUPS.map((group) => (
          <section key={group.title} className="mt-14">
            <h2 className="font-display text-2xl tracking-tight">{group.title}</h2>
            <p className="mt-2 max-w-[60ch] text-sm text-muted-foreground">
              {group.blurb}
            </p>
            <ul className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {group.slugs
                .map((s) => BEST_BY_SLUG[s])
                .filter(Boolean)
                .map((l) => (
                  <li key={l.slug}>
                    <a
                      href={`/library/best/${l.slug}`}
                      className="flex h-full flex-col rounded-2xl border border-border p-5 transition-colors hover:bg-muted"
                    >
                      <h3 className="font-display text-lg tracking-tight">{l.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                        {l.metaDescription}
                      </p>
                    </a>
                  </li>
                ))}
            </ul>
          </section>
        ))}

        <section className="mt-16 flex flex-wrap gap-3 text-sm">
          <Link
            to="/library"
            className="inline-flex min-h-[40px] items-center rounded-full border border-border px-4 transition-colors hover:bg-muted"
          >
            Search all gems
          </Link>
          <a
            href="/library/new"
            className="inline-flex min-h-[40px] items-center rounded-full border border-border px-4 transition-colors hover:bg-muted"
          >
            Newest additions
          </a>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
